import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Typography, CircularProgress, Box } from '@mui/material';

import { useTransfersPage } from '../hooks/useTransfersPage';
import { TransfersFilterBar } from '../components/transfers/TransfersFilterBar';
import { TransfersTable, buildColumns } from '../components/transfers/TransfersTable';
import { TransfersSummaryPanel } from '../components/transfers/TransfersSummaryPanel';

import { ConfirmDialog } from '../components/transfers/ConfirmDialog';
import { DenyDialog } from '../components/transfers/DenyDialog';
import { EditDialog } from '../components/transfers/EditDialog';
import { PreviewImagesDialog } from '../components/transfers/PreviewImagesDialog';
import { usePending } from '../context/PendingContext';

import axios from '../api/axios';

export default function Transfers() {
  const userId = Number(localStorage.getItem('user_id') || '');

  const data = useTransfersPage(userId);

  const {
    rows, rowCount, loading,
    paginationModel, setPaginationModel,
    sortModel, setSortModel,
    searchText, setSearchText,
    filterType, setFilterType,
    filterSite, setFilterSite,
    filterStartDate, setFilterStartDate,
    filterEndDate, setFilterEndDate,
    sites, summary,
    transferFromSummary, transferToSummary,
    finishedProjectByDate,
    transferTypeLabels,
    fetchTransfers,
    resetFilter
  } = data;

  // ✨ NEW: Auto-mark read

  useEffect(() => {
    // Collect all rows that this user created and hasn't read yet
    console.log("mark-read", rows)
    const unreadIds = rows
      .filter(r => Number(r.updated_by) === userId && r.creator_read_status === 0)
      .map(r => r.id);
    console.log("unreadIds", unreadIds)
    if (unreadIds.length > 0) {
      // Background process: wait a bit so UI paints colors, then mark read
      // Next time data loads, it won't be unread anymore.
      axios.post('/transfers/mark-read', { ids: unreadIds })
        .then(() => {
          console.log("mark-read success")
          // Also refresh the overall pending badge in Layout
          refreshPending();
        })
        .catch(err => console.error('Failed to mark read', err));
    }
  }, [rows, userId]); // refreshPending omitted defensively as it's from context and might cause loops if not memoized, though we can safely just ignore its dependency warning for background task

  //
  // ─── Dialog States ───────────────────────────────────────────
  //

  // let pending_to_do = localStorage.getItem('pending_to_do');

  // Confirm dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmRemark, setConfirmRemark] = useState('');
  const [confirmFiles, setConfirmFiles] = useState([]);
  const [confirmError, setConfirmError] = useState('');
  const [savingConfirm, setSavingConfirm] = useState(false);
  const { refreshPending } = usePending();

  const handleConfirm = (row) => {
    setConfirmId(row.id);
    setConfirmRemark('');
    setConfirmFiles([]);
    setConfirmError('');
    setConfirmOpen(true);
  };

  const saveConfirm = async () => {
    try {
      setSavingConfirm(true);

      await axios.put(`/transfers/${confirmId}`, {
        action: 'confirm',
        remark: confirmRemark || undefined,
      });

      if (confirmFiles.length) {
        const fd = new FormData();
        confirmFiles.forEach(f => fd.append('files', f));
        await axios.post(`/transfers/${confirmId}/attachments`, fd);
      }
      // localStorage.setItem('pending_to_do', pending_to_do - 1);
      // pending_to_do =- 1;

      await fetchTransfers();
      setConfirmOpen(false);
    } catch (err) {
      const msg = err.response?.data?.message || '確認失敗';
      if (msg.includes('Insufficient stock')) {
        setConfirmError(
          `目前庫存不足! 現有：${err.response.data.have}，需求：${err.response.data.need}`
        );
      } else {
        alert(msg);
      }
    } finally {
      await refreshPending();
      setSavingConfirm(false);
    }
  };

  // Deny dialog
  const [denyOpen, setDenyOpen] = useState(false);
  const [denyId, setDenyId] = useState(null);
  const [denyRemark, setDenyRemark] = useState('');
  const [denyFiles, setDenyFiles] = useState([]);
  const [savingDeny, setSavingDeny] = useState(false);

  const handleDeny = (row) => {
    setDenyId(row.id);
    setDenyRemark('');
    setDenyFiles([]);
    setDenyOpen(true);
  };

  const saveDeny = async () => {
    if (!denyRemark.trim()) return alert("請填寫拒絕原因");

    try {
      setSavingDeny(true);
      const fd = new FormData();
      fd.append('remark', denyRemark);
      denyFiles.forEach(f => fd.append('files', f));

      // localStorage.setItem('pending_to_do', pending_to_do - 1);
      // pending_to_do =- 1;
      await axios.post(`/transfers/${denyId}/deny-with-remark`, fd);
      await fetchTransfers();
      setDenyOpen(false);
    } catch {
      alert('拒絕失敗');
    } finally {
      await refreshPending();
      setSavingDeny(false);
    }
  };

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editPairUpCode, setEditPairUpCode] = useState('');
  const [editRemark, setEditRemark] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const handleEdit = (row) => {
    setEditId(row.id);
    setEditPairUpCode(row.pair_up_code || '');
    setEditRemark(row.remark || '');
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      setSavingEdit(true);
      await axios.put(`/transfers/${editId}`, {
        action: 'edit',
        pair_up_code: editPairUpCode,
        remark: editRemark,
      });
      await fetchTransfers();
      setEditOpen(false);
    } finally {
      setSavingEdit(false);
    }
  };

  // Preview images dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const handlePreviewImages = async (transferId) => {
    setPreviewImages([]);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const res = await axios.get(`/transfers/${transferId}/attachments`);
      setPreviewImages(res.data);
    } finally {
      setPreviewLoading(false);
    }
  };

  //
  // ─── TABLE COLUMNS ───────────────────────────────────────────
  //
  const columns = buildColumns({
    userId,
    transferTypeLabels,
    onEdit: handleEdit,
    onConfirm: handleConfirm,
    onDeny: handleDeny,
    onPreviewImages: handlePreviewImages,
  });

  //
  // ─── UI ──────────────────────────────────────────────────────
  //
  return (
    <Layout>
      <Typography variant="h5" sx={{ ml: 1, mb: 2 }}>貨物進出紀錄</Typography>

      {/* FILTER BAR */}
      <TransfersFilterBar
        searchText={searchText}
        setSearchText={setSearchText}
        filterType={filterType}
        setFilterType={setFilterType}
        filterSite={filterSite}
        setFilterSite={setFilterSite}
        filterStartDate={filterStartDate}
        setFilterStartDate={setFilterStartDate}
        filterEndDate={filterEndDate}
        setFilterEndDate={setFilterEndDate}
        sites={sites}
        transferTypeLabels={transferTypeLabels}
        onSearch={() => { setPaginationModel({ ...paginationModel, page: 0 }); fetchTransfers(); }}
        onReset={resetFilter}
      />

      {/* SUMMARY PANEL */}
      <TransfersSummaryPanel
        summary={summary}
        transferTypeLabels={transferTypeLabels}
        filterSite={filterSite}
        transferFromSummary={transferFromSummary}
        transferToSummary={transferToSummary}
        finishedProjectByDate={finishedProjectByDate}
      />

      {/* TABLE */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TransfersTable
          rows={rows}
          rowCount={rowCount}
          loading={loading}
          columns={columns}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          sortModel={sortModel}
          setSortModel={setSortModel}
        />
      )}

      {/* DIALOGS */}
      <ConfirmDialog
        open={confirmOpen}
        remark={confirmRemark}
        setRemark={setConfirmRemark}
        files={confirmFiles}
        setFiles={setConfirmFiles}
        onRemoveFile={(i) => setConfirmFiles(prev => prev.filter((_, idx) => idx !== i))}
        errorMessage={confirmError}
        saving={savingConfirm}
        onClose={() => setConfirmOpen(false)}
        onSubmit={saveConfirm}
      />

      <DenyDialog
        open={denyOpen}
        remark={denyRemark}
        setRemark={setDenyRemark}
        files={denyFiles}
        setFiles={setDenyFiles}
        onRemoveFile={(i) => setDenyFiles(prev => prev.filter((_, idx) => idx !== i))}
        saving={savingDeny}
        onClose={() => setDenyOpen(false)}
        onSubmit={saveDeny}
      />

      <EditDialog
        open={editOpen}
        pairUpCode={editPairUpCode}
        remark={editRemark}
        setPairUpCode={setEditPairUpCode}
        setRemark={setEditRemark}
        saving={savingEdit}
        onClose={() => setEditOpen(false)}
        onSubmit={saveEdit}
      />

      <PreviewImagesDialog
        open={previewOpen}
        images={previewImages}
        loading={previewLoading}
        onClose={() => setPreviewOpen(false)}
      />
    </Layout>
  );
}
